import dashboardCsService from "@services/dashboard/dashboardCsService"
// `import type` là bắt buộc: interface được dùng trong chữ ký có decorator, và với
// isolatedModules + emitDecoratorMetadata thì import thường sẽ lỗi TS1272.
import type {
  ICsMyTasks,
  ICsMyTasksPerStaff,
  IFeeBatchSummary,
  IFeeDueSummary,
  IFeeOverdueAging,
  ICustomerChangeSummary,
  IFeedbackSlaSummary,
  IHandoverDayCapacity,
  IHandoverOnTime,
  IPinkBookFeeSummary,
  IPinkBookPipeline,
  INewCustomerSummary,
  IReminderEffectiveness,
  IStaffWorkload,
} from "@services/dashboard/dashboardCsService"
import { action, makeAutoObservable, observable } from "mobx"

/**
 * Store của Dashboard CS (Feature-0197). Mỗi widget có bộ ba `data*` / `loading*` / `error*` riêng:
 * dashboard load song song và một widget lỗi KHÔNG được kéo sập các widget khác, nên trạng thái lỗi
 * phải tách theo từng widget thay vì dùng một cờ chung.
 */
class DashboardCsStore {
  @observable dataMyTasks?: ICsMyTasks
  @observable loadingMyTasks = false
  @observable errorMyTasks = false

  @observable dataFeeDueSummary?: IFeeDueSummary
  @observable loadingFeeDueSummary = false
  @observable errorFeeDueSummary = false

  @observable dataFeeBatchSummary?: IFeeBatchSummary
  @observable loadingFeeBatchSummary = false
  @observable errorFeeBatchSummary = false

  @observable dataFeeOverdueAging?: IFeeOverdueAging
  @observable loadingFeeOverdueAging = false
  @observable errorFeeOverdueAging = false

  @observable dataReminderEffectiveness?: IReminderEffectiveness
  @observable loadingReminderEffectiveness = false
  @observable errorReminderEffectiveness = false

  @observable dataPinkBookPipeline?: IPinkBookPipeline
  @observable loadingPinkBookPipeline = false
  @observable errorPinkBookPipeline = false

  @observable dataPinkBookFeeSummary?: IPinkBookFeeSummary
  @observable loadingPinkBookFeeSummary = false
  @observable errorPinkBookFeeSummary = false

  @observable dataHandoverOnTime?: IHandoverOnTime
  @observable loadingHandoverOnTime = false
  @observable errorHandoverOnTime = false

  @observable dataHandoverCapacity: IHandoverDayCapacity[] = []
  @observable loadingHandoverCapacity = false
  @observable errorHandoverCapacity = false

  @observable dataFeedbackSla?: IFeedbackSlaSummary
  @observable loadingFeedbackSla = false
  @observable errorFeedbackSla = false

  @observable dataCustomerChange?: ICustomerChangeSummary
  @observable loadingCustomerChange = false
  @observable errorCustomerChange = false

  @observable dataNewCustomer?: INewCustomerSummary
  @observable loadingNewCustomer = false
  @observable errorNewCustomer = false

  @observable dataTeamWorkload: IStaffWorkload[] = []
  @observable loadingTeamWorkload = false
  @observable errorTeamWorkload = false

  @observable dataTeamTasks: ICsMyTasksPerStaff[] = []
  @observable loadingTeamTasks = false
  @observable errorTeamTasks = false

  constructor() {
    makeAutoObservable(this)
  }

  @action
  getMyTasks = async (): Promise<void> => {
    this.loadingMyTasks = true
    this.errorMyTasks = false
    try {
      this.dataMyTasks = await dashboardCsService.getMyTasks()
    } catch {
      // Nuốt lỗi ở đây là CÓ CHỦ Ý: widget tự hiển thị trạng thái lỗi + nút thử lại riêng của nó.
      // Nếu để throw ra ngoài thì scene phải bắt, và một widget lỗi sẽ làm trắng cả trang.
      this.errorMyTasks = true
    } finally {
      this.loadingMyTasks = false
    }
  }

  @action
  getFeeDueSummary = async (
    params?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingFeeDueSummary = true
    this.errorFeeDueSummary = false
    try {
      this.dataFeeDueSummary = await dashboardCsService.getFeeDueSummary(params)
    } catch {
      this.errorFeeDueSummary = true
    } finally {
      this.loadingFeeDueSummary = false
    }
  }

  @action
  getFeeBatchSummary = async (
    params?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingFeeBatchSummary = true
    this.errorFeeBatchSummary = false
    try {
      this.dataFeeBatchSummary =
        await dashboardCsService.getFeeNotificationBatchSummary(params)
    } catch {
      this.errorFeeBatchSummary = true
    } finally {
      this.loadingFeeBatchSummary = false
    }
  }

  @action
  getFeeOverdueAging = async (
    params?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingFeeOverdueAging = true
    this.errorFeeOverdueAging = false
    try {
      this.dataFeeOverdueAging =
        await dashboardCsService.getFeeOverdueAging(params)
    } catch {
      this.errorFeeOverdueAging = true
    } finally {
      this.loadingFeeOverdueAging = false
    }
  }

  @action
  getReminderEffectiveness = async (
    params?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingReminderEffectiveness = true
    this.errorReminderEffectiveness = false
    try {
      this.dataReminderEffectiveness =
        await dashboardCsService.getReminderEffectiveness(params)
    } catch {
      this.errorReminderEffectiveness = true
    } finally {
      this.loadingReminderEffectiveness = false
    }
  }

  @action
  getPinkBookPipeline = async (
    params?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingPinkBookPipeline = true
    this.errorPinkBookPipeline = false
    try {
      this.dataPinkBookPipeline =
        await dashboardCsService.getPinkBookPipeline(params)
    } catch {
      this.errorPinkBookPipeline = true
    } finally {
      this.loadingPinkBookPipeline = false
    }
  }

  @action
  getPinkBookFeeSummary = async (
    params?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingPinkBookFeeSummary = true
    this.errorPinkBookFeeSummary = false
    try {
      this.dataPinkBookFeeSummary =
        await dashboardCsService.getPinkBookFeeSummary(params)
    } catch {
      this.errorPinkBookFeeSummary = true
    } finally {
      this.loadingPinkBookFeeSummary = false
    }
  }

  @action
  getHandoverOnTime = async (
    params?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingHandoverOnTime = true
    this.errorHandoverOnTime = false
    try {
      this.dataHandoverOnTime =
        await dashboardCsService.getHandoverOnTimeRate(params)
    } catch {
      this.errorHandoverOnTime = true
    } finally {
      this.loadingHandoverOnTime = false
    }
  }

  @action
  getHandoverCapacity = async (
    params?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingHandoverCapacity = true
    this.errorHandoverCapacity = false
    try {
      this.dataHandoverCapacity =
        await dashboardCsService.getHandoverWeeklyCapacity(params)
    } catch {
      this.errorHandoverCapacity = true
    } finally {
      this.loadingHandoverCapacity = false
    }
  }

  @action
  getFeedbackSla = async (params?: Record<string, unknown>): Promise<void> => {
    this.loadingFeedbackSla = true
    this.errorFeedbackSla = false
    try {
      this.dataFeedbackSla =
        await dashboardCsService.getFeedbackSlaSummary(params)
    } catch {
      this.errorFeedbackSla = true
    } finally {
      this.loadingFeedbackSla = false
    }
  }

  @action
  getCustomerChangeSummary = async (
    params?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingCustomerChange = true
    this.errorCustomerChange = false
    try {
      this.dataCustomerChange =
        await dashboardCsService.getCustomerChangeRequestSummary(params)
    } catch {
      this.errorCustomerChange = true
    } finally {
      this.loadingCustomerChange = false
    }
  }

  @action
  getNewCustomerSummary = async (
    params?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingNewCustomer = true
    this.errorNewCustomer = false
    try {
      this.dataNewCustomer =
        await dashboardCsService.getNewCustomerSummary(params)
    } catch {
      this.errorNewCustomer = true
    } finally {
      this.loadingNewCustomer = false
    }
  }

  @action
  getTeamWorkload = async (params?: Record<string, unknown>): Promise<void> => {
    this.loadingTeamWorkload = true
    this.errorTeamWorkload = false
    try {
      this.dataTeamWorkload = await dashboardCsService.getTeamWorkload(params)
    } catch {
      this.errorTeamWorkload = true
    } finally {
      this.loadingTeamWorkload = false
    }
  }

  @action
  getTeamTasks = async (params?: Record<string, unknown>): Promise<void> => {
    this.loadingTeamTasks = true
    this.errorTeamTasks = false
    try {
      this.dataTeamTasks = await dashboardCsService.getTeamTasks(params)
    } catch {
      this.errorTeamTasks = true
    } finally {
      this.loadingTeamTasks = false
    }
  }
}

export default DashboardCsStore
